import { Component, ViewChild, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { GoogleMap, MapMarker } from "@angular/google-maps";
import { Router, ActivatedRoute, RouterLink } from "@angular/router";

import { AssetSummaryComponent } from "../asset-summary/asset-summary.component";
import { ApiService } from "src/app/api.service";
import { FleetTrackerService } from "src/app/fleet-tracker.service";
import { mapStyles } from "src/app/mapstyles";
import { DatetimePipe } from "src/app/pipes";
import { StickyHeaderComponent } from "src/app/shared/sticky-header/sticky-header.component";
import { APIResults, AssemblyConfig } from "src/app/sharedinterfaces";
declare const BrandVars;

@Component({
	selector: "app-fleet-default",
	templateUrl: "./fleet-default.component.html",
	styleUrls: ["./fleet-default.component.scss"],
	imports: [RouterLink, StickyHeaderComponent, FormsModule, GoogleMap, MapMarker, AssetSummaryComponent]
})
export class FleetDefaultComponent {
	private fleettracker = inject(FleetTrackerService);
	private api = inject(ApiService);
	private router = inject(Router);
	private route = inject(ActivatedRoute);

	@ViewChild("googleMap", { static: false })
	googleMap: GoogleMap;

	public fleet?: APIResults.Fleet;
	public sortedAssets: APIResults.FleetAsset[] = [];
	public sortType = "tail";
	public brand = BrandVars;
	public markers: AssetMarker[];
	public bounds: google.maps.LatLngBounds;
	public mapFitted = false;
	public mapOptions = {
		styles: mapStyles,
		disableDefaultUI: true,
		zoomControl: false,
		// cameraControl: true,
		fullscreenControl: true,
		gestureHandling: "greedy",
		keyboardShortcuts: false,
	};

	private infoWindow = new google.maps.InfoWindow();

	constructor() {
		this.fleettracker.currentFleet.pipe(takeUntilDestroyed()).subscribe(currentfleet => {
			this.fleet = currentfleet;
			this.sortChange(this.sortType);
			this.populateMap();
		});
	}

	get allFixed(): boolean {
		if (!this.fleet || !this.fleet.assets) return true;
		return this.fleet.assets.every(asset => asset.is_fixed);
	}

	sortChange(sort: string) {
		this.sortType = sort;
		if (this.fleet && this.fleet.assets && this.fleet.assets.length > 0) {
			switch (sort) {
				case "tail":
					this.sortedAssets = this.fleet.assets.slice().sort((a, b) => {
						if (a.organization_name > b.organization_name) { return 1; }
						if (a.organization_name < b.organization_name) { return -1; }
						if (a.asset_name > b.asset_name) { return 1; }
						if (a.asset_name < b.asset_name) { return -1; }
						return 0;
					});
					break;
				case "type":
					this.sortedAssets = this.fleet.assets.slice().sort((a, b) => {
						// Should always be true
						if (a.asset_type_name && b.asset_type_name) {
							if (a.asset_type_name > b.asset_type_name) { return 1; }
							if (a.asset_type_name < b.asset_type_name) { return -1; }
						}
						return 0;
					});
					break;
				case "health":
					this.sortedAssets = this.fleet.assets.slice().sort((a, b) => {
						if (a.totalhimax < b.totalhimax) { return 1; }
						if (a.totalhimax > b.totalhimax) { return -1; }
						return 0;
					});
					break;
				default:
					this.sortedAssets = this.fleet.assets;
			}
		} else {
			this.sortedAssets = [];
		}
	}

	private async populateMap(): Promise<void> {
		if (this.allFixed) return;
		this.markers = undefined;
		this.bounds = undefined;
		if (this.fleet && this.fleet.assets) {
			const points = await this.api.GetFleetMap(this.fleet.fleet_id);
			const bounds = new google.maps.LatLngBounds();
			const markers = [];
			for (const point of points) {
				bounds.extend(point);
				const asset = this.fleet.assets.find(a => a.asset_id === point.asset_id);
				let iconUrl = "/brand/img/marker-ok.png";
				if (asset) {
					if (asset.totalhimax >= AssemblyConfig.HumsHiThreshold.Alarm) {
						iconUrl = "/brand/img/marker-alarm.png";
					} else if (asset.totalhimax >= AssemblyConfig.HumsHiThreshold.Warning) {
						iconUrl = "/brand/img/marker-warning.png";
					}
				}
				markers.push({
					mapPoint: point,
					markerOptions: {
						animation: null,
						opacity: 1 - (point.index / 10),
						icon: {
							url: iconUrl,
							scaledSize: new google.maps.Size(30, 30),
							anchor: new google.maps.Point(15, 30),
							labelOrigin: new google.maps.Point(15, 40),
						},
						label: {
							text: asset ? asset.asset_name : "",
							color: "#CCC",
							fontSize: "12px",
							fontWeight: "bold",
							className: "map-label",
						},
					}
				});
			}
			this.mapFitted = false;
			this.markers = markers;
			this.bounds = bounds;
			this.mapLoaded();
		}
	}

	public mapLoaded() {
		if (this.googleMap && this.bounds && !this.mapFitted) {
			this.googleMap.fitBounds(this.bounds);
			// Never start zoomed in too far
			this.googleMap.googleMap.setZoom(Math.min(this.googleMap.googleMap.getZoom(), 11));
			this.mapFitted = true;
		}
	}

	public assetHover(asset: APIResults.FleetAsset) {
		if (!this.markers) return;
		// This should find the first (current) marker if multiple breadcrumbs
		const marker = this.markers.find(m => m.mapPoint.asset_id === asset.asset_id);
		if (marker) {
			const newopts = structuredClone(marker.markerOptions);
			const newicon = newopts.icon as google.maps.Icon;
			newicon.scaledSize = new google.maps.Size(40, 40);
			newicon.anchor = new google.maps.Point(20, 40);
			newicon.labelOrigin = new google.maps.Point(20, 50);
			(newopts.label as google.maps.MarkerLabel).color = "#FFF";
			newopts.zIndex = 1000;
			// Kludgey way to force a refresh, replace the whole options
			marker.markerOptions = newopts;
		}
	}

	public assetUnHover(asset: APIResults.FleetAsset) {
		if (!this.markers) return;
		const marker = this.markers.find(m => m.mapPoint.asset_id === asset.asset_id);
		if (marker) {
			const newopts = structuredClone(marker.markerOptions);
			const newicon = newopts.icon as google.maps.Icon;
			newicon.scaledSize = new google.maps.Size(30, 30);
			newicon.anchor = new google.maps.Point(15, 30);
			newicon.labelOrigin = new google.maps.Point(15, 40);
			(newopts.label as google.maps.MarkerLabel).color = "#CCC";
			newopts.zIndex = 0;
			// Kludgey way to force a refresh, replace the whole options
			marker.markerOptions = newopts;
		}
	}

	public markerClick(mapPoint: APIResults.FleetMapPoint) {
		this.router.navigate(["asset", mapPoint.asset_id], { relativeTo: this.route });
	}

	public markerMouseover(event: google.maps.MapMouseEvent, marker: AssetMarker) {
		let content = `<div class="asset-name">${marker.mapPoint.asset_name}</div>`;
		if (marker.mapPoint.ts) {
			const formatted = new DatetimePipe().transform(marker.mapPoint.ts as any, "offset", this.fleet ? this.fleet.time_offset_hours : 0);
			content += `<div>${formatted}</div>`;
		}
		content += "<div>&nbsp;</div>";
		if (marker.mapPoint.spd !== undefined) {
			content += `<div><span class="head">Speed:</span> ${marker.mapPoint.spd} kn</div>`;
		}
		if (marker.mapPoint.hdg !== undefined) {
			content += `<div><span class="head">Heading:</span> ${marker.mapPoint.hdg}°</div>`;
		}
		this.infoWindow = new google.maps.InfoWindow({
			disableAutoPan: true,
			headerDisabled: true,
			position: event.latLng,
			pixelOffset: new google.maps.Size(0, -30),
			content: `<div class="breadcrumb-tooltip">${content}</div>`,
		} as any);
		this.infoWindow.open(this.googleMap.googleMap);
	}
	public markerMouseout() {
		this.infoWindow.close();
	}
}

interface AssetMarker {
	mapPoint: APIResults.FleetMapPoint;
	markerOptions: google.maps.MarkerOptions;
}